import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { testIdPart } from '@/lib/testIds';

import type { CategoryTab } from './documentLabels';

interface CategoryTabsProps {
  tabs: CategoryTab[];
  /** The chosen tab's id: a category id, `'all'` or `'uncategorized'`. */
  value: string;
  onChange: (id: string) => void;
  /** True before the counts have arrived: the names show without them. */
  loading?: boolean;
  testId: string;
}

/**
 * The library's category tabs (P1): All Documents, one per category,
 * Uncategorized, each with its count in its name so a screen reader hears
 * "Voter Resources (6)". MUI's `Tabs` supplies the tab list role and the
 * arrow keys; scrollable, so twenty categories stay reachable.
 */
export function CategoryTabs({
  tabs,
  value,
  onChange,
  loading = false,
  testId,
}: CategoryTabsProps) {
  // A chosen category that no longer exists must not break the tab list, so
  // the value falls back to the first tab until the caller clears it.
  const known = tabs.some((tab) => tab.id === value) ? value : (tabs[0]?.id ?? false);
  return (
    <Tabs
      value={known}
      onChange={(_event, next: string) => onChange(next)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label="Categories"
      data-testid={testId}
      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          value={tab.id}
          label={loading ? tab.name : `${tab.name} (${tab.count})`}
          data-testid={`${testId}-tab-${testIdPart(tab.id)}`}
          sx={{ textTransform: 'none' }}
        />
      ))}
    </Tabs>
  );
}
